import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LocalizedLink, useLocalizedNav } from "@/i18n/LocalizedLink";
import { toast } from "sonner";
import { Lock, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LangContext";

export default function ResetPassword() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useLocalizedNav();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setLoading(true);
    try {
      await api.post("/users/reset-password", { token, new_password: form.password });
      setDone(true);
      toast.success(t("auth.reset_done"));
      setTimeout(() => nav("/signin"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <main className="min-h-[60vh] grid place-items-center p-6 text-center" data-testid="reset-no-token">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-3">Invalid reset link</h1>
          <LocalizedLink to="/forgot-password" className="text-brand-cyan hover:text-white">{t("auth.forgot")}</LocalizedLink>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-[80vh] grid place-items-center p-6 relative" data-testid="reset-page">
      <div className="aurora opacity-50" />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-brand to-brand-cyan glow-blue mb-4">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-2">{t("auth.reset_title")}</h1>
          <p className="text-sm text-zinc-400">{t("auth.reset_sub")}</p>
        </div>

        {done ? (
          <div className="bg-[#0c0c0c] border border-brand-green/30 rounded-xl p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-brand-green mx-auto mb-3" />
            <p className="text-zinc-300">{t("auth.reset_done")}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-[#0c0c0c] border border-white/5 rounded-xl p-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2 block">{t("auth.new_password")}</label>
              <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full bg-[#080808] border border-white/10 rounded-lg px-4 py-3 focus:border-brand outline-none" data-testid="reset-password" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2 block">{t("auth.confirm_password")}</label>
              <input required type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="w-full bg-[#080808] border border-white/10 rounded-lg px-4 py-3 focus:border-brand outline-none" data-testid="reset-confirm" />
            </div>
            <button type="submit" disabled={loading} className="w-full bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-3 rounded-lg glow-blue transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="reset-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {t("auth.update_password")}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
